import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, BoxExtendedProps, MissingDetailIcon, StepDoneIcon, Text } from '@common-fe/common-fe';

import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import { useMyMoneyFormStore } from '@/modules/UseMyMoney/stores';
import { ClaimSummaryField, USE_MY_MONEY_UPLOAD_STORE_KEY, UseMyMoneyOption } from '@/modules/UseMyMoney/UseMyMoney.types';
import DocumentMayBeRequiredWarning from '@/modules/UseMyMoney/widgets/UseMyMoneySummary/components/DocumentSkippedWarning/DocumentMayBeRequiredWarning';
import OcrScanFailedWarning from '@/modules/UseMyMoney/widgets/UseMyMoneySummary/components/OcrScanFailedWarning/OcrScanFailedWarning';
import useClaimSummaryFields from '@/modules/UseMyMoney/widgets/UseMyMoneySummary/hooks/useClaimSummaryFields';

import ClaimSummaryFieldCard from './ClaimSummaryFieldCard';
import ConfettiAnimation from './ConfettiAnimation';

interface Props extends BoxExtendedProps {
  useMyMoneyOption?: UseMyMoneyOption;
  setActiveField?: (filed?: ClaimSummaryField) => void;
  onUploadFile: () => void;
  hasAvailableAccounts?: boolean;
}

const ClaimSummaryFields: React.FC<Props> = ({
  useMyMoneyOption,
  setActiveField,
  onUploadFile,
  hasAvailableAccounts,
  ...props
}) => {
  const { t } = useTranslation();
  const { isAnimationPlayed, handleSetState } = useMyMoneyFormStore();
  const { values: { files } } = useFilesInDocumentStore(USE_MY_MONEY_UPLOAD_STORE_KEY);

  const [confettiOrigin, setConfettiOrigin] = useState<DOMRect | null>(null);
  const iconRef = useRef<HTMLDivElement>(null);

  const {
    filledFields,
    hasFilledFields,
    emptyFields,
    hasEmptyFields,
  } = useClaimSummaryFields({
    useMyMoneyType: useMyMoneyOption,
    hasAvailableAccounts,
  });

  useEffect(() => {
    if (!hasEmptyFields && iconRef.current) {
      setConfettiOrigin(iconRef.current.getBoundingClientRect());
    }
  }, [hasEmptyFields]);

  const onAnimationComplete = useCallback(() => {
    handleSetState({ isAnimationPlayed: true });
    setConfettiOrigin(null);
  }, [handleSetState]);

  const showDocumentMayBeRequiredWarning = useMemo(() => !files?.length, [files?.length]);

  const showOcrScanFailedWarning = useMemo(() => !!files?.length && hasEmptyFields, [files?.length, hasEmptyFields]);

  return (
    <>
      <Box data-testId="claim-summary-fields" gap="spacing24" {...props}>
        {showDocumentMayBeRequiredWarning && !hasEmptyFields && useMyMoneyOption !== UseMyMoneyOption.HSA_WITHDRAWAL && (
          <DocumentMayBeRequiredWarning onUpload={onUploadFile} />
        )}

        {(showDocumentMayBeRequiredWarning || showOcrScanFailedWarning) && hasEmptyFields && (
          <Box data-testId="claim-summary-empty-fields_wrapper">
            <Box data-testId="claim-summary-empty-fields_title" direction="row" align="center">
              <MissingDetailIcon color="missingDetail" size="24px" />
              <Text margin={{ left: 'spacing4' }} weight={700}>
                {t('Required data')}
              </Text>
            </Box>

            {showDocumentMayBeRequiredWarning && useMyMoneyOption !== UseMyMoneyOption.HSA_WITHDRAWAL && (
              <Box margin={{ top: 'spacing8', bottom: 'spacing8' }}>
                <DocumentMayBeRequiredWarning onUpload={onUploadFile} />
              </Box>
            )}

            {showOcrScanFailedWarning && useMyMoneyOption !== UseMyMoneyOption.HSA_WITHDRAWAL && (
              <Box margin={{ top: 'spacing8', bottom: 'spacing8' }}>
                <OcrScanFailedWarning onUpload={onUploadFile} />
              </Box>
            )}

            <Box data-testId="claim-summary-empty-fields" margin={{ top: 'spacing8' }} gap="spacing2">
              {emptyFields?.map((field) => (
                <ClaimSummaryFieldCard
                  key={field.key}
                  filedKey={field.key as ClaimSummaryField}
                  title={field?.title}
                  description={field?.description as string}
                  value={field?.value as string}
                  setActiveField={setActiveField}
                  node={field?.node}
                />
              ))}
            </Box>
          </Box>
        )}

        {hasFilledFields && (
          <Box data-testId="claim-summary-filled-fields_wrapper">
            <Box data-testId="claim-summary-filled-fields_title" direction="row" align="center">
              <Box ref={iconRef}>
                <StepDoneIcon size="24px" />
              </Box>
              <Text margin={{ left: 'spacing4' }} weight={700}>
                {t('Ready to submit')}
              </Text>
            </Box>

            <Box data-testId="claim-summary-filled-fields" margin={{ top: 'spacing8' }} gap="spacing2">
              {filledFields?.map((field) => (
                <ClaimSummaryFieldCard
                  key={field.key}
                  filedKey={field.key as ClaimSummaryField}
                  title={field?.title}
                  description={field?.description as string}
                  value={field?.value as string}
                  setActiveField={setActiveField}
                  node={field?.node}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {confettiOrigin && !isAnimationPlayed && (
        <ConfettiAnimation
          originRect={confettiOrigin}
          onComplete={onAnimationComplete}
        />
      )}
    </>
  );
};

export default ClaimSummaryFields;
